// src/pages/auth/VerifyOTP.js
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import loginImage from '../../assets/images/profile.png';
import { authAPI } from '../../api/apiConfig';

export default function VerifyOTP() {
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handleChange = (element, index) => {
        if (isNaN(element.value)) return false;
        
        const newOtp = [...otp];
        newOtp[index] = element.value;
        setOtp(newOtp);
        
        // Focus next input
        if (element.value !== '' && element.nextSibling) {
            element.nextSibling.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const prevInput = e.target.previousSibling;
            if (prevInput) prevInput.focus();
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        const enteredOtp = otp.join('');
        
        if (enteredOtp.length !== 6) {
            setError('Please enter complete 6-digit OTP');
            return;
        }
        
        // Get mobile from localStorage
        const mobile = localStorage.getItem('adminMobile');
        if (!mobile) {
            setError('Mobile number not found. Please login again.');
            navigate('/');
            return;
        }
        
        // ONLY accept admin mobile for now
        if (mobile !== '+91-9490633344') {
            setError('Access Denied! Only admin can verify OTP.');
            return;
        }
        
        setLoading(true);
        setError('');
        
        try {
            // Call API to verify OTP
            const response = await authAPI.verifyOTP(mobile, enteredOtp);
            
            if (response.success && response.token) {
                // Save authentication data
                authAPI.saveAuthData(response);
                
                // Clear mobile from localStorage (now stored in auth data)
                localStorage.removeItem('adminMobile');
                
                // Navigate to dashboard
                navigate('/home');
            } else {
                setError(response.message || 'OTP verification failed');
            }
        } catch (error) {
            console.error('Verify OTP error:', error);
            setError(error.message || 'OTP verification failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        const mobile = localStorage.getItem('adminMobile') || '+91-9490633344';
        
        // Check if it's admin mobile
        if (mobile !== '+91-9490633344') {
            alert('Access Denied! Only admin can receive OTP.');
            return;
        }
        
        setLoading(true);
        setError('');
        
        try {
            const response = await authAPI.sendOTP(mobile);
            
            if (response.success) {
                alert('OTP has been resent successfully');
                // Reset OTP inputs
                setOtp(['', '', '', '', '', '']);
            } else {
                setError(response.message || 'Failed to resend OTP');
            }
        } catch (error) {
            console.error('Resend OTP error:', error);
            setError('Failed to resend OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container-fluid login-container" style={{ minHeight: '100vh' }}>
            <div className="row">
                {/* Left Side: Form */}
                <div className="col-lg-6 d-flex align-items-center justify-content-center">
                    <div className="w-100 px-lg-5 px-4 py-4" style={{ maxWidth: '420px' }}>
                        <h3 className="mb-3 fw-bold">Admin OTP Verification 🔐</h3>
                        <p className="text-muted mb-4">
                            Enter OTP sent to your mobile
                        </p>
                        
                        {/* Admin OTP Notice */}
                        <div className="alert alert-warning mb-3">
                            <small>
                                <strong>Admin OTP Only:</strong><br/>
                                Mobile: <code>+91-9490633344</code><br/>
                                OTP: <code>123456</code>
                            </small>
                        </div>
                        
                        {/* Error Message */}
                        {error && (
                            <div className="alert alert-danger">
                                <small>{error}</small>
                            </div>
                        )}
                        
                        {/* OTP Form */}
                        <form onSubmit={handleVerify}>
                            <div className="mb-4">
                                <label className="form-label mb-3">Enter 6-digit OTP</label>
                                <div className="d-flex justify-content-between gap-2">
                                    {otp.map((data, index) => (
                                        <input
                                            key={index}
                                            type="text"
                                            className="form-control text-center"
                                            maxLength="1"
                                            value={data}
                                            onChange={(e) => handleChange(e.target, index)}
                                            onKeyDown={(e) => handleKeyDown(e, index)}
                                            onFocus={(e) => e.target.select()}
                                            style={{ height: '60px', fontSize: '1.5rem' }}
                                            required
                                        />
                                    ))}
                                </div>
                                <div className="text-center mt-3">
                                    <button 
                                        type="button" 
                                        className="btn btn-link"
                                        onClick={handleResendOTP}
                                        disabled={loading}
                                    >
                                        Resend OTP
                                    </button>
                                </div>
                            </div>
                            
                            <div className="d-grid gap-2 mt-4">
                                <button 
                                    type="submit" 
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <span className="d-flex align-items-center justify-content-center gap-2">
                                            <span className="spinner-border spinner-border-sm" role="status"></span>
                                            Verifying...
                                        </span>
                                    ) : 'Verify & Continue'}
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-outline-secondary"
                                    onClick={() => navigate('/')}
                                    disabled={loading}
                                >
                                    Back
                                </button>
                            </div>
                        </form>
                        
                        <div className="mt-4">
                            <div className="alert alert-info">
                                <small>
                                    <strong>Note:</strong> OTP is valid for 10 minutes.
                                    Make sure you're using admin mobile number.
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Right Side: Image */}
                <div className="col-lg-6 d-none d-lg-block p-0 overflow-hidden">
                    <div className="d-flex justify-content-start">
                        <img 
                            style={{ objectFit: 'cover', height: '100vh' }} 
                            src={loginImage} 
                            alt="OTP verification illustration" 
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}